import { EncodingType, Encodings, FontNames, Font as PDFFont } from '@pdf-lib/standard-fonts'
import { Font, Size } from './rendering'

type MeasureTextOpts = {
  font: Font
  lineHeight: number
  kerning?: false
}

interface TextMeasurementProvider {
  measureText(text: string, opts: MeasureTextOpts): Size
}

export class StandardFontTextMeasurementProvider implements TextMeasurementProvider {
  private fontCache = new Map<FontNames, PDFFont>()
  private readonly encoding: EncodingType = Encodings.WinAnsi

  private readonly missingGlyphWidth = 1000

  measureText(text: string, opts: MeasureTextOpts): Size {
    const { font, lineHeight, kerning } = opts

    if (kerning) {
      throw new Error('Kerning is not supported')
    }

    const pdfFont = this.loadFont(font.family as FontNames)

    let maxWidth = 0
    const lines = text.split('\n')
    for (const line of lines) {
      const w = this.widthNoKerning(line, pdfFont, font.size)
      if (w > maxWidth) maxWidth = w
    }

    return {
      width: maxWidth,
      height: lines.length * font.size * lineHeight
    }
  }

  /* ---------- helpers -------------------------------------------------- */

  private loadFont(name: FontNames): PDFFont {
    let f = this.fontCache.get(name)
    if (!f) {
      f = PDFFont.load(name)
      this.fontCache.set(name, f)
    }
    return f
  }

  private encode(text: string) {
    const glyphs: { name: string }[] = []
    for (const ch of Array.from(text)) {
      const cp = ch.codePointAt(0)!
      if (this.encoding.canEncodeUnicodeCodePoint(cp)) {
        glyphs.push(this.encoding.encodeUnicodeCodePoint(cp))
      } else {
        // Character cannot be encoded in the selected encoding. Substitute with a
        // ".notdef" glyph so that measurement can continue and we fall back to the
        // configured missing-glyph width (1 em).
        glyphs.push({ name: '.notdef' })
      }
    }
    return glyphs
  }

  private getGlyphWidth(font: PDFFont, name: string): number {
    const w = font.getWidthOfGlyph(name)
    if (!w || name === '.notdef') return this.missingGlyphWidth
    return w
  }

  private widthNoKerning(line: string, font: PDFFont, size: number): number {
    return (
      (this.encode(line).reduce((sum, g) => sum + this.getGlyphWidth(font, g.name), 0) * size) /
      1000
    )
  }
}

const TEXT_MEASUREMENT_PROVIDER = new StandardFontTextMeasurementProvider()

export function measureText(text: string, opts: MeasureTextOpts): Size {
  return TEXT_MEASUREMENT_PROVIDER.measureText(text, opts)
}
